import React from 'react';
import Icon from '../../../components/AppIcon';


const TrustSignalsSection = () => {
  // Static values for social proof (no database queries for optimal performance)
  const clientCount = 50; // Update manually as needed
  const quizzesToday = 8; // Average daily quizzes
  const certificationCategories = [
    {
      id: 1,
      title: "Entrenamiento Personal y Fitness",
      icon: "Dumbbell",
      color: "from-primary to-primary/80",
      certifications: [
        "Experta FEDA en Entrenamiento Personal (Level I y II)",
        "Experta FEDA en Sala Fitness y Wellness",
        "Experta FEDA en Entrenamiento Funcional",
        "Experta FEDA en Fundamentos de la Motricidad Humana"
      ],
      year: "2015-2017",
      organization: "FEDA - Federación Española de AADD y Fitness"
    },
    {
      id: 2,
      title: "Nutrición Especializada",
      icon: "Apple",
      color: "from-success to-success/80",
      certifications: [
        "Experta FEDA en Nutrición y Dietética Deportiva",
        "Programa Experto \"Haz que tu dieta funcione\" - Nutritional Coaching Center®"
      ],
      year: "2015-2024",
      organization: "FEDA & Nutritional Coaching Center® (validez internacional)"
    },
    {
      id: 3,
      title: "Salud Maternal y Postparto",
      icon: "Heart",
      color: "from-accent to-accent/80",
      certifications: [
        "Certificada FEDA en Pilates Mat y Lesiones",
        "Certificada FEDA en Prescripción de Pilates durante el Embarazo",
        "Certificada FEDA en Acondicionamiento Físico Postparto",
        "Certificada FEDA en Suelo Pélvico aplicado al Fitness",
        "Seminario de Pilates y Embarazo - Federación Española de Pilates"
      ],
      year: "2015-2019",
      organization: "FEDA & Federación Española de Pilates"
    }
  ];

  const trustBadges = [
    {
      id: 1,
      title: "Cumplimiento GDPR",
      description: "Tus datos están protegidos según la normativa europea",
      icon: "Lock",
      color: "text-success"
    },
    {
      id: 2,
      title: "SSL Certificado",
      description: "Conexión segura y encriptada",
      icon: "Shield",
      color: "text-primary"
    },
    {
      id: 3,
      title: "Política de Privacidad",
      description: "Transparencia total en el manejo de información",
      icon: "Eye",
      color: "text-accent"
    }
  ];

  const liveStats = [
    {
      id: 1,
      label: "Evaluaciones completadas hoy",
      value: quizzesToday,
      icon: "TrendingUp",
      trend: "+12%"
    },
    {
      id: 2,
      label: "Mujeres en programa actualmente",
      value: clientCount,
      icon: "Users",
      trend: "+8%"
    },
    {
      id: 3,
      label: "Años promedio reducidos este mes",
      value: 11.3,
      icon: "Clock",
      trend: "+2.1"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-muted/20 to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-6">
            Respaldado por{' '}
            <span className="text-primary font-heading-bold">Certificaciones Oficiales (y Vitales)</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Más de 10 años de experiencia, mi propia vivencia como madre de dos, y una formación constante en salud, nutrición, fitness y bienestar femenino.
          </p>
        </div>

        {/* Certifications */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {certificationCategories?.map((category) => (
            <div
              key={category?.id}
              className="bg-card rounded-2xl shadow-card-shadow border border-border p-6 hover:shadow-card-elevated transition-smooth"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${category?.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                <Icon name={category?.icon} size={32} className="text-primary-foreground" />
              </div>

              <h3 className="font-body-semibold text-lg text-foreground mb-3 text-center">
                {category?.title}
              </h3>

              <ul className="space-y-2 mb-4 text-left">
                {category?.certifications?.map((cert, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={14} className="text-success mt-1 flex-shrink-0" />
                    <span className="font-caption text-xs text-muted-foreground leading-relaxed">
                      {cert}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="pt-3 border-t border-border">
                <p className="font-caption text-xs text-muted-foreground mb-2 text-center">
                  {category?.organization}
                </p>
                <div className="inline-flex items-center px-3 py-1 bg-primary/10 rounded-full w-full justify-center">
                  <Icon name="Calendar" size={12} className="text-primary mr-1" />
                  <span className="font-caption text-xs text-primary font-medium">
                    {category?.year}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Certifications Note */}
        <div className="text-center mb-16">
          <p className="text-base text-muted-foreground max-w-3xl mx-auto">
            👉 Cada certificación no es solo un papel. Es una herramienta que me permite acompañarte con rigor, empatía y seguridad en cada etapa de tu vida.
          </p>
        </div>

        {/* Live Stats */}
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-3xl p-8">
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl text-foreground mb-2">
              Estadísticas en Tiempo Real
            </h3>
            <p className="font-caption text-sm text-muted-foreground">
              Actualizado cada 5 minutos • Última actualización: {new Date()?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {liveStats?.map((stat) => (
              <div key={stat?.id} className="bg-card rounded-xl shadow-card-shadow border border-border p-6 text-center">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                    <Icon name={stat?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-heading-bold text-2xl text-foreground">
                      {stat?.value?.toLocaleString('es-ES')}
                    </span>
                    <div className="flex items-center text-success text-sm">
                      <Icon name="TrendingUp" size={12} className="mr-1" />
                      <span className="font-mono">{stat?.trend}</span>
                    </div>
                  </div>
                </div>
                
                <p className="font-caption text-sm text-muted-foreground">
                  {stat?.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Trust Elements */}
        <div className="flex flex-wrap justify-center items-center gap-8 mt-16 pt-8 border-t border-border">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span className="font-caption text-sm">Madrid, España</span>
          </div>
          
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Phone" size={16} />
            <span className="font-caption text-sm">+34 900 123 456</span>
          </div>
          
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Mail" size={16} />
            <span className="font-caption text-sm">hola@comeyvivesano.com</span>
          </div>
          
          <div className="flex items-center space-x-2 text-success">
            <Icon name="CheckCircle" size={16} />
            <span className="font-caption text-sm font-medium">Empresa Verificada</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSignalsSection;